import { bind, wire } from 'hyperhtml/esm';
import { KatalComponent } from '../../shared/base';

/**
 * @component {kat-breadcrumb} KatalBreadcrumb Breadcrumbs is a graphical control element used as a navigational aid in user interfaces. It allows users to keep track of their locations within websites.
 * @guideline Breadcrumbs should always appear at the top of a page.
 * @guideline Dont It is not recommended to use breadcrumbs in multiple places on a page or in the middle of a page.
 * @contentProvided This is to let users know the components expects in inner html to be completed
 * @example SetViaJson {"items": [{"label":"Parent", "href":"#"}, {"label":"Group", "href":"#"},{"label":"Category", "href":"#"},{"label":"Current Category", "href":"#"}], "content":""}
 * @example SetViaHtml {"content": "<kat-breadcrumb-item label=\"Parent\" href=\"#\"></kat-breadcrumb-item><kat-breadcrumb-item label=\"Group\" href=\"#\"></kat-breadcrumb-item><kat-breadcrumb-item label=\"Category\" href=\"#\"></kat-breadcrumb-item><kat-breadcrumb-item label=\"Current Category\" href=\"#\" last></kat-breadcrumb-item>"}
 * @status Production
 * @theme flo
 */
class KatalBreadcrumb extends KatalComponent {
  static get observedAttributes() {
    return ['items'];
  }

  constructor() {
    super(KatalBreadcrumb.observedAttributes);
  }

  /**
   * @param {json} items The array of breadcrumb group items
   */
  get items() {
    var items = [];
    this.querySelectorAll('kat-breadcrumb-item').forEach(b =>
      items.push(b.value)
    );
    return items;
  }

  set items(value) {
    if (Array.isArray(value)) {
      this._items = value;
      this.render();
    } else if (typeof value === 'string') {
      try {
        this.items = value === '' ? [] : JSON.parse(value);
      } catch (err) {
        console.error('Items must be set as a valid JSON - ' + err);
      }
    } else {
      console.error('Items must be set as an Array');
    }
  }

  render() {
    return bind(this)`
            ${this._items.map((item, i) => this.renderItem(item, i))}
        `;
  }

  renderItem(item, i) {
    const isLastItem = i === this._items.length - 1;
    return wire(item)`
            <kat-breadcrumb-item label=${item.label} href=${item.href} last=${isLastItem}>
            </kat-breadcrumb-item>
        `;
  }
}

/**
 *
 */
class KatalBreadcrumbItem extends KatalComponent {
  static get observedAttributes() {
    return ['label', 'href', 'last'];
  }

  constructor() {
    super(KatalBreadcrumbItem.observedAttributes);
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  /**
   * The text displayed for the breadcrumb item.
   */
  get label() {
    return this.getAttribute('label') || '';
  }

  set label(value) {
    this.setAttribute('label', value);
  }

  /**
   * The target of the navigation to be sent to
   */
  get href() {
    return this.getAttribute('href') || '';
  }

  set href(value) {
    this.setAttribute('href', value);
  }

  /**
   * determines if this is the last bread crumb item in the list.
   */
  get last() {
    return this.getBooleanAttribute('last');
  }

  set last(value) {
    this.setBooleanAttribute('last', value);
  }

  /**
   * The object representation of this BreadCrumbItem
   * {
   *   "label": "value",
   *   "href": "value",
   *   "last":false
   * }
   */
  get value() {
    return {
      label: this.label,
      href: this.href,
      last: this.last,
    };
  }

  render() {
    return this.last
      ? bind(this)`<span>${this.label}</span>`
      : bind(this)`
                <kat-link href="${this.href}" label="${this.label}"></kat-link>
                <kat-icon name="chevron-right" size="small"></kat-icon>
            `;
  }

  // Respond to attribute changes.
  attributeChangedCallback() {
    this.render();
  }
}

KatalComponent.register('kat-breadcrumb', KatalBreadcrumb);
KatalComponent.register('kat-breadcrumb-item', KatalBreadcrumbItem);
